import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const ClientCustomFields = ({ clientId, customFieldValues = {}, onCustomFieldsChange, readOnly = false }) => {
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [sections, setSections] = useState([]);
  const [fieldValues, setFieldValues] = useState(customFieldValues);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchCustomFieldData();
  }, []);

  useEffect(() => {
    setFieldValues(customFieldValues);
  }, [customFieldValues]);

  const fetchCustomFieldData = async () => {
    try {
      setLoading(true);
      
      // Fetch both fields and sections
      const [fieldsResponse, sectionsResponse] = await Promise.all([
        api.get('/custom-fields/entity/client'),
        api.get('/custom-fields/sections/entity/client')
      ]);
      
      setCustomFieldDefinitions(fieldsResponse.data);
      setSections(sectionsResponse.data);
      
      // Initialize expanded state for sections
      const initialExpanded = {};
      sectionsResponse.data.forEach(section => {
        initialExpanded[section.id] = section.is_expanded_by_default;
      });
      setExpandedSections(initialExpanded);
      
    } catch (error) {
      console.error('Error fetching custom field data:', error);
      setError('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    const newValues = {
      ...fieldValues,
      [fieldName]: value
    };
    
    setFieldValues(newValues);
    
    // Clear validation error for this field
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
    
    // Notify parent component of changes
    if (onCustomFieldsChange) {
      onCustomFieldsChange(newValues);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const groupFieldsBySection = () => {
    const grouped = {
      noSection: [],
      sections: {}
    };

    // Initialize sections
    sections.forEach(section => {
      grouped.sections[section.id] = {
        ...section,
        fields: []
      };
    });

    // Group fields
    customFieldDefinitions.forEach(field => {
      if (field.section_id && grouped.sections[field.section_id]) {
        grouped.sections[field.section_id].fields.push(field);
      } else {
        grouped.noSection.push(field);
      }
    });

    return grouped;
  };

  const validateField = async (fieldDefinition, value) => {
    try {
      const response = await api.post('/custom-fields/validate', {
        fieldId: fieldDefinition.id,
        value: value
      });
      
      return response.data;
    } catch (error) {
      console.error('Error validating field:', error);
      return { isValid: false, error: 'Validation failed' };
    }
  };

  const validateAllFields = async () => {
    const errors = {};
    
    for (const fieldDef of customFieldDefinitions) {
      const value = fieldValues[fieldDef.field_name];
      const validation = await validateField(fieldDef, value);
      
      if (!validation.isValid) {
        errors[fieldDef.field_name] = validation.error;
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const renderField = (fieldDefinition) => {
    const { field_name, display_label, field_type, field_options, is_required, help_text } = fieldDefinition;
    const value = fieldValues[field_name] || '';
    const hasError = validationErrors[field_name];

    const baseInputClasses = `w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
      hasError ? 'border-red-300' : 'border-gray-300'
    } ${readOnly ? 'bg-gray-50' : ''}`;

    switch (field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field_name, e.target.value)}
            className={baseInputClasses}
            placeholder={field_options.placeholder || ''}
            maxLength={field_options.maxLength}
            disabled={readOnly}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field_name, e.target.value)}
            className={baseInputClasses}
            min={field_options.min}
            max={field_options.max}
            step={field_options.step || 1}
            disabled={readOnly}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field_name, e.target.value)}
            className={baseInputClasses}
            disabled={readOnly}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleFieldChange(field_name, e.target.checked)}
              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded shadow-sm"
              disabled={readOnly}
            />
            <div className="flex-1">
              <span className="text-base font-medium text-gray-900">
                {display_label}
              </span>
              {help_text && (
                <p className="text-sm text-gray-600 mt-1">{help_text}</p>
              )}
            </div>
          </div>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field_name, e.target.value)}
            className={baseInputClasses}
            disabled={readOnly}
          >
            <option value="">Select {display_label}</option>
            {field_options.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field_name, e.target.value)}
            className={baseInputClasses}
            disabled={readOnly}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (customFieldDefinitions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 text-sm">No custom fields configured</p>
      </div>
    );
  }

  const groupedFields = groupFieldsBySection();

  const renderFieldsGrid = (fields) => {
    // Sort fields by their sort_order to maintain proper sequence
    const sortedFields = [...fields].sort((a, b) => a.sort_order - b.sort_order);

    return (
      <div className="space-y-6">
        {sortedFields.map((fieldDef) => (
          <div key={fieldDef.id} className="space-y-1">
            {fieldDef.field_type !== 'boolean' && (
              <label className="block text-sm font-medium text-gray-700">
                {fieldDef.display_label}
                {fieldDef.is_required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            
            {renderField(fieldDef)}
            
            {fieldDef.field_type !== 'boolean' && fieldDef.help_text && (
              <p className="text-xs text-gray-500">{fieldDef.help_text}</p>
            )}
            
            {validationErrors[fieldDef.field_name] && (
              <p className="text-xs text-red-600">{validationErrors[fieldDef.field_name]}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSection = (section) => {
    const isExpanded = expandedSections[section.id];
    const hasFields = section.fields.length > 0;

    if (!hasFields) return null;

    return (
      <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
        {section.is_collapsible ? (
          <button
            onClick={() => toggleSection(section.id)}
            className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
          >
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {section.display_label}
              </h3>
              {section.description && (
                <p className="text-sm text-gray-600 mt-1">{section.description}</p>
              )}
            </div>
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
        ) : (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {section.display_label}
            </h3>
            {section.description && (
              <p className="text-sm text-gray-600 mt-1">{section.description}</p>
            )}
          </div>
        )}
        
        {(!section.is_collapsible || isExpanded) && (
          <div className="px-6 py-6">
            {renderFieldsGrid(section.fields)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Render sections */}
      {Object.values(groupedFields.sections)
        .sort((a, b) => a.section_order - b.section_order)
        .map(renderSection)}
      
      {/* Render fields without sections */}
      {groupedFields.noSection.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Additional Information</h3>
          {renderFieldsGrid(groupedFields.noSection)}
        </div>
      )}
    </div>
  );
};

// Export validation function for use in parent components
export const validateCustomFields = async (customFieldValues) => {
  try {
    const response = await api.post('/custom-fields/validate-multiple', {
      entityType: 'client',
      customFieldValues: customFieldValues
    });
    
    return response.data;
  } catch (error) {
    console.error('Error validating custom fields:', error);
    return { isValid: false, fieldValidations: {} };
  }
};

export default ClientCustomFields; 